import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { SITE } from "../data/siteData";

const CDN = "https://images.squarespace-cdn.com/content/v1/5e1fdc831acd51128a859d90";
const W = "?format=1000w";

const TEAM = [
  {
    name: "Dylan Rowse",
    role: "Owner and Head Trainer",
    images: [
      `${CDN}/c4f854c1-66cb-4d05-bce0-de44dee5d68c/FR7A2697.JPG${W}`,
      `${CDN}/91b1b339-80ed-477b-84fb-2894c0845e4f/FR7A5814.jpg${W}`,
      `${CDN}/a9e24f76-46ff-4626-988e-b7cf8c67ce67/FR7A7984.jpg${W}`,
    ],
    bio: [
      "My name is Dylan Rowse and I'm the founder of Chinook K-9. I have always had an incredible passion for dogs, as well as a strong desire to help others.",
      "When I was introduced to the world of both sport and pet dog training, I knew I had found my calling.",
      "I have spent the last half decade furthering my skills and education as a professional dog trainer, learning from industry leading professionals, and training hundreds of dogs and owners successfully.",
      "In 2023 I had the privilege of certifying under 2x World IGP Champion Ivan Balabanov's revolutionary Training Without Conflict system of dog training — the first in Western Canada to do so.",
      "I have made it my life's goal to help others find the same level of success in their relationships with their dogs that I've found with my own.",
    ],
  },
  {
    name: "Katelan Virostek",
    role: "Trainer",
    images: [
      `${CDN}/2d9274c1-265c-4cdb-a397-03681ca0d571/FR7A8703.jpg${W}`,
      `${CDN}/3cb9cc71-ec86-4755-91e0-e58783b0fc95/FR7A8667.jpg${W}`,
      `${CDN}/80b5c63e-c8a3-41d9-a212-4b9797e9fd52/FR7A8596.jpg${W}`,
    ],
    bio: [
      "As a kid, I was captivated by the way our Border Collies moved through the fields, confidently working cattle on our family farm. Watching them in action sparked a deep love for dogs and an appreciation for their intelligence, drive, and loyalty.",
      "Earlier this year, I graduated from Medicine Hat College's Service Dog and Canine Studies program, where I spent two years training service dogs and diving deep into canine psychology and behavior. During my two-month practicum at the Taber Lost Paws Society, I discovered that my true passion lies in pet dog training — helping everyday dogs become happy, well-mannered companions.",
      "I originally applied for a kennel tech position at Chinook K9, and after just a few months, I'm proud to now be working as a trainer — turning my passion into a career I truly love.",
      "There's nothing more rewarding to me than helping people connect with their dogs on a deeper level. Whether it's basic puppy training or improving obedience, I love seeing the transformation that happens when training helps owners and their dogs truly understand each other. My goal is to help people enjoy life with their dogs — at home, on adventures, or wherever life takes them.",
    ],
  },
  {
    name: "Zack Rickett",
    role: "Trainer",
    images: [
      `${CDN}/8341f442-ad1f-44f6-9544-9200d7fa4483/FR7A8794.jpg${W}`,
      `${CDN}/ea7092e2-d73c-4bdb-998d-009028cac8df/FR7A8867.jpg${W}`,
      `${CDN}/c8aa8118-d3e1-4b07-a9d1-0d99a4fa7f50/FR7A8879.jpg${W}`,
    ],
    bio: [
      "Hey, my names Zack! I'm a professional dog trainer with a deep passion for canine psychology and behavioural science! I really enjoy unlocking the minds of the dogs I work with and bringing out their best qualities!",
      "Growing up in the UK, I was exposed to a surplus of reactive, unsocialised and ill mannered dogs with no outlet. Watching owners struggle and stigmas build quickly sparked my enthusiasm. That's where the journey begins!",
      "After moving to Canada with my personal dog, Rockstar, I decided I wanted to train on a professional level and got serious!",
      "I worked at multiple high capacity daycare/boarding facilities in Calgary where I earned my stripes. Socialising large groups of dogs taught me a lot about dog psychology and behaviour!",
      "I reached out to Dylan Rowse when I found out he had trained under 2x world champion Ivan Balabanov and was TWC certified! I knew I wanted to work with the very best!",
      "The rest is history! I treat every day with gratitude and feel very lucky to have found myself where I am today! I'm currently living my dream, training dogs and helping owners understand their beloved pets!",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us | Meet the Trainers | Chinook K9 Calgary"
        description="Meet the Chinook K9 team — Dylan Rowse, Katelan Virostek and Zack Rickett. Certified professional dog trainers serving Calgary and surrounding areas."
        path="/about"
      />

      <section className="relative min-h-[55vh] flex items-end overflow-hidden" data-testid="about-hero">
        <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_dog-trainer-yeg/artifacts/b8scvr7l_image.png" alt="Three trained dogs resting on a mountainside in Alberta" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/25 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-44 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4 hero-text-shadow">About Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05] hero-text-shadow">
              Your goals are our mission.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="about-mission">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="text-lg text-white/80 leading-relaxed">
              Using humane, forward thinking and industry leading methods of dog training we are here to support you
              and your dog as you learn to live the life you've always dreamed! Whether you're welcoming a new furry
              friend to the family, seeking help with behavioural issues, or looking to take your training to the next
              level, we're committed to helping all dogs and owners overcome obstacles and achieve their goals!
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian pb-24" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-16">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Our Team</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
              Meet the trainers
            </h2>
          </Reveal>

          <div className="space-y-24">
            {TEAM.map((member, i) => (
              <div
                key={member.name}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 ? "" : ""}`}
                data-testid={`team-${member.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Reveal className={i % 2 ? "lg:order-2" : ""}>
                  <div className="grid grid-cols-2 gap-4">
                    <img src={member.images[0]} alt={`${member.name}, ${member.role} at Chinook K9`}
                      className="col-span-2 rounded-2xl w-full h-auto border border-white/10" loading="lazy" />
                    <img src={member.images[1]} alt={`${member.name} training`} className="rounded-2xl w-full h-auto border border-white/10" loading="lazy" />
                    <img src={member.images[2]} alt={`${member.name} with dogs`} className="rounded-2xl w-full h-auto border border-white/10" loading="lazy" />
                  </div>
                </Reveal>
                <Reveal delay={0.15} className={i % 2 ? "lg:order-1" : ""}>
                  <h3 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white uppercase">{member.name}</h3>
                  <p className="text-brand font-heading font-bold text-sm tracking-widest uppercase mt-2 mb-6">{member.role}</p>
                  <div className="space-y-4">
                    {member.bio.map((p, j) => (
                      <p key={j} className="text-white/70 leading-relaxed">{p}</p>
                    ))}
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#121212] border-t border-white/10 py-20" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Start your training journey today!</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/contact" data-testid="about-contact-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Contact Us <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} data-testid="about-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                <Phone size={16} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
